import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

/**
 * Tooltip component that shows text on hover
 * @param {Object} props
 * @param {React.ReactNode} props.children - Element to attach tooltip to
 * @param {string} props.text - Text to show in tooltip
 * @param {string} props.position - Tooltip position: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {boolean} props.showArrow - Whether to show the arrow pointer (default: true)
 */
const Tooltip = ({
  children,
  text,
  position = 'top',
  showArrow = true,
  bypassLengthGate = false
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [actualPosition, setActualPosition] = useState(position); // Track actual rendered position
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  // Reset actual position when tooltip shows
  useEffect(() => {
    if (showTooltip) {
      setActualPosition(position);
    }
  }, [showTooltip, position]);

  useEffect(() => {
    if (showTooltip && triggerRef.current) {
      const calculatePosition = () => {
        if (!triggerRef.current) return;
        
        // getBoundingClientRect() returns viewport coordinates (not document coordinates)
        // Since we use position: fixed, we need viewport coordinates directly
        const rect = triggerRef.current.getBoundingClientRect();

        // Estimate tooltip dimensions based on text
        const estimatedCharWidth = 7.5; // Average character width in pixels
        const estimatedLineHeight = 20;
        const padding = 24; // 12px left + 12px right
        const verticalPadding = 16; // 8px top + 8px bottom
        const arrowHeight = 6;
        
        const textWidth = Math.min(text.length * estimatedCharWidth, 300);
        const tooltipWidth = Math.max(textWidth + padding, 80);
        const tooltipHeight = verticalPadding + estimatedLineHeight + arrowHeight;

        let top, left;
        const spacing = 6; // Space between element and tooltip

        switch (position) {
          case 'top':
            top = rect.top - tooltipHeight - spacing;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'bottom':
            top = rect.bottom + spacing;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'left':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.left - tooltipWidth - spacing;
            break;
          case 'right':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.right + spacing;
            break;
          default:
            top = rect.bottom + spacing;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        }

        // Adjust if tooltip goes outside viewport
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 10;

        // Horizontal adjustments
        if (left < margin) {
          left = margin;
        } else if (left + tooltipWidth > viewportWidth - margin) {
          left = viewportWidth - tooltipWidth - margin;
        }

        // Track actual position for arrow rendering
        let finalPosition = position;
        
        // Vertical adjustments - if tooltip would go off screen, flip to opposite side
        if (top < margin) {
          // If top position doesn't fit, try bottom
          if (position === 'top') {
            top = rect.bottom + spacing;
            finalPosition = 'bottom';
          } else {
            top = margin;
          }
        } else if (top + tooltipHeight > viewportHeight - margin) {
          // If bottom position doesn't fit, try top
          if (position === 'bottom') {
            top = rect.top - tooltipHeight - spacing;
            finalPosition = 'top';
            // If still doesn't fit, constrain to viewport
            if (top < margin) {
              top = viewportHeight - tooltipHeight - margin;
              finalPosition = 'bottom'; // Keep bottom if top doesn't fit either
            }
          } else {
            top = viewportHeight - tooltipHeight - margin;
          }
        }

        setTooltipPosition({ top, left });
        setActualPosition(finalPosition);
      };

      // Initial calculation
      calculatePosition();

      // Recalculate after a brief delay to get actual tooltip dimensions
      const timeoutId = setTimeout(() => {
        if (tooltipRef.current && triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();

          let top, left;
          const spacing = 6;

          switch (position) {
            case 'top':
              top = rect.top - tooltipRect.height - spacing;
              left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
              break;
            case 'bottom':
              top = rect.bottom + spacing;
              left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
              break;
            case 'left':
              top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
              left = rect.left - tooltipRect.width - spacing;
              break;
            case 'right':
              top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
              left = rect.right + spacing;
              break;
            default:
              top = rect.bottom + spacing;
              left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
          }

          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const margin = 10;

          // Horizontal adjustments
          if (left < margin) {
            left = margin;
          } else if (left + tooltipRect.width > viewportWidth - margin) {
            left = viewportWidth - tooltipRect.width - margin;
          }

          // Track actual position for arrow rendering
          let finalPosition = position;

          // Vertical adjustments - if tooltip would go off screen, flip to opposite side
          if (top < margin) {
            // If top position doesn't fit, try bottom
            if (position === 'top') {
              top = rect.bottom + spacing;
              finalPosition = 'bottom';
            } else {
              top = margin;
            }
          } else if (top + tooltipRect.height > viewportHeight - margin) {
            // If bottom position doesn't fit, try top
            if (position === 'bottom') {
              top = rect.top - tooltipRect.height - spacing;
              finalPosition = 'top';
              // If still doesn't fit, constrain to viewport
              if (top < margin) {
                top = viewportHeight - tooltipRect.height - margin;
                finalPosition = 'bottom'; // Keep bottom if top doesn't fit either
              }
            } else {
              top = viewportHeight - tooltipRect.height - margin;
            }
          }

          setTooltipPosition({ top, left });
          setActualPosition(finalPosition);
        }
      }, 0);

      // Recalculate on scroll to keep tooltip positioned correctly
      const handleScroll = () => {
        if (showTooltip && triggerRef.current) {
          calculatePosition();
        }
      };

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [showTooltip, position, text]);

  if (!text) {
    return <>{children}</>;
  }

  if (!bypassLengthGate && text.length <= 12) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={
          bypassLengthGate
            ? { display: 'block', width: '100%', minWidth: 0 }
            : { display: 'inline-block' }
        }
      >
        {children}
      </span>
      {showTooltip && createPortal(
        <div
          ref={tooltipRef}
          className={`tooltip tooltip-${actualPosition}`}
          style={{
            position: 'fixed',
            top: tooltipPosition ? `${tooltipPosition.top}px` : '-9999px',
            left: tooltipPosition ? `${tooltipPosition.left}px` : '-9999px',
            zIndex: 10000,
            visibility: tooltipPosition ? 'visible' : 'hidden'
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="tooltip-content">{text}</div>
          {showArrow && actualPosition === 'bottom' && <div className="tooltip-arrow tooltip-arrow-top"></div>}
          {showArrow && actualPosition === 'top' && <div className="tooltip-arrow tooltip-arrow-bottom"></div>}
          {showArrow && actualPosition === 'left' && <div className="tooltip-arrow tooltip-arrow-right"></div>}
          {showArrow && actualPosition === 'right' && <div className="tooltip-arrow tooltip-arrow-left"></div>}
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
